/**
 * T-053 — PDPA contracts (tasks/W4-CONTRACTS.md §6 · docs/10-design/13 §5–6)
 * DSAR workflow, discovery/export, anonymize, retention, ROPA, incidents
 */
import { z } from "zod";

export const DsarType = z.enum(["access", "portability", "rectify", "erase", "restrict", "object", "withdraw_consent"]);
export type DsarType = z.infer<typeof DsarType>;

export const DsarSource = z.enum(["manual", "portal", "idp", "email"]);
export type DsarSource = z.infer<typeof DsarSource>;

export const DsarStatus = z.enum(["new", "verifying", "in_progress", "awaiting_approval", "approved", "delivered", "closed", "rejected"]);
export type DsarStatus = z.infer<typeof DsarStatus>;

export const DsarArtifact = z.object({
  r2_key: z.string(),
  kind: z.string(), // "json" | "zip"
  bytes: z.number().int().nonnegative(),
  expires_at: z.iso.datetime(),
});
export type DsarArtifact = z.infer<typeof DsarArtifact>;

export const DsarArtifactWithUrl = DsarArtifact.extend({ url: z.string().nullable() });
export type DsarArtifactWithUrl = z.infer<typeof DsarArtifactWithUrl>;

export const DsarRequest = z.object({
  id: z.uuid(),
  subject_email: z.string().nullable(),
  subject_user_id: z.uuid().nullable(),
  type: DsarType,
  source: DsarSource,
  received_at: z.iso.datetime(),
  due_at: z.iso.datetime(),
  status: DsarStatus,
  handler_user_id: z.uuid().nullable(),
  approver_user_id: z.uuid().nullable(),
  delivered_at: z.iso.datetime().nullable(),
  artifacts: z.array(DsarArtifact),
  notes: z.string().nullable(),
  closed_at: z.iso.datetime().nullable(),
  updated_at: z.iso.datetime(),
});
export type DsarRequest = z.infer<typeof DsarRequest>;

export const DsarInput = z
  .object({
    subject_email: z.email().optional(),
    subject_user_id: z.uuid().optional(),
    type: DsarType,
    notes: z.string().max(2000).optional(),
  })
  .refine((v) => v.subject_email !== undefined || v.subject_user_id !== undefined, { message: "ต้องระบุ subject_email หรือ subject_user_id" });
export type DsarInput = z.infer<typeof DsarInput>;

export const DsarListQuery = z.object({
  status: DsarStatus.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type DsarListQuery = z.infer<typeof DsarListQuery>;

export const DsarTransition = z.object({
  to: DsarStatus,
  note: z.string().max(2000).optional(),
});
export type DsarTransition = z.infer<typeof DsarTransition>;

/** API-origin outbound metadata only; an assertion is not proof of identity or delivery. */
export const SubjectOutboundEmail = z.object({
  id: z.string(),
  client_id: z.uuid(),
  provider: z.string().nullable(),
  provider_message_id: z.string().nullable(),
  stream: z.string(),
  status: z.string(),
  created_at: z.iso.datetime(),
  scheduled_at: z.iso.datetime().nullable(),
  queued_at: z.iso.datetime().nullable(),
  sent_at: z.iso.datetime().nullable(),
  completed_at: z.iso.datetime().nullable(),
  expires_at: z.iso.datetime().nullable(),
  matched_via: z.array(z.enum(["related_user_sso_sub", "recipient_email"])),
});
export type SubjectOutboundEmail = z.infer<typeof SubjectOutboundEmail>;

export const SubjectDiscoveryQuery = z.object({
  outbound_limit: z.coerce.number().int().min(1).max(500).default(100),
  outbound_cursor: z.string().min(1).max(1024).optional(),
}).strict();
export const SubjectOutboundPage = z.object({
  next_cursor: z.string().nullable(), has_more: z.boolean(), snapshot_at: z.iso.datetime(),
});
export type SubjectOutboundPage = z.infer<typeof SubjectOutboundPage>;

/** Discovery — ทุกอย่างของบุคคลในระบบ (13 §5.2) · ใช้ทั้ง access/export/erase */
export const SubjectDiscovery = z.object({
  outbound_emails: z.array(SubjectOutboundEmail).optional(),
  outbound_page: SubjectOutboundPage.optional(),
  users: z.array(z.object({ id: z.uuid(), primary_email: z.string(), display_name: z.string().nullable(), kind: z.string(), status: z.string() })),
  emails: z.array(z.string()),
  tickets: z.array(
    z.object({
      id: z.uuid(),
      public_id: z.string(),
      subject: z.string(),
      via: z.array(z.enum(["requester", "cc", "mention"])),
      legal_hold: z.boolean(),
      anonymized_at: z.iso.datetime().nullable(),
    }),
  ),
  messages: z.array(z.object({ id: z.uuid(), ticket_id: z.uuid(), seq: z.number().int(), kind: z.string(), direction: z.string(), at: z.iso.datetime() })),
  attachments: z.array(z.object({ id: z.uuid(), ticket_id: z.uuid().nullable(), filename: z.string(), size: z.number().int(), status: z.string() })),
  guest_links: z.array(z.object({ id: z.uuid(), ticket_id: z.uuid(), purpose: z.string(), revoked_at: z.iso.datetime().nullable() })),
  sessions: z.array(z.object({ id: z.string(), kind: z.string(), expires_at: z.iso.datetime(), revoked_at: z.iso.datetime().nullable() })),
  consents: z.array(z.object({ id: z.uuid(), form_id: z.uuid().nullable(), channel: z.string(), given_at: z.iso.datetime(), withdrawn_at: z.iso.datetime().nullable() })),
  csat: z.array(z.object({ id: z.uuid(), ticket_id: z.uuid(), score: z.number().int(), at: z.iso.datetime() })),
  audit_refs: z.object({ count: z.number().int() }),
});
export type SubjectDiscovery = z.infer<typeof SubjectDiscovery>;

export const DsarExportResult = z.object({
  dsar_id: z.uuid(),
  status: z.enum(["done", "queued"]),
  artifacts: z.array(DsarArtifactWithUrl),
  ticket_count: z.number().int(),
});
export type DsarExportResult = z.infer<typeof DsarExportResult>;

export const AnonymizeInput = z.object({
  dry_run: z.boolean(),
});
export type AnonymizeInput = z.infer<typeof AnonymizeInput>;

export const AnonymizeSummary = z.object({
  users_anonymized: z.number().int(),
  emails_removed: z.number().int(),
  tickets: z.number().int(),
  tickets_skipped_legal_hold: z.number().int(),
  messages_anonymized: z.number().int(),
  participants_anonymized: z.number().int(),
  custom_fields_cleared: z.number().int(),
  attachments_deleted: z.number().int(),
  raw_eml_deleted: z.number().int(),
  guest_links_revoked: z.number().int(),
  sessions_revoked: z.number().int(),
  /** [#176] side_messages ของ subject ที่ถูก pseudonymize (บทสนทนาบนการ์ดที่ไม่ legal_hold) */
  side_messages_anonymized: z.number().int(),
  /** [#176] side_conversations ที่ participants มีอีเมล subject และถูกแทนด้วย anon */
  side_participants_anonymized: z.number().int(),
});
export type AnonymizeSummary = z.infer<typeof AnonymizeSummary>;

export const AnonymizeResult = z.object({
  dry_run: z.boolean(),
  summary: AnonymizeSummary,
  /** true = เหลือการ์ดอีก → ต่อด้วย scheduled_actions kind=dsar_anonymize ใน cron retentionTask */
  scheduled: z.boolean(),
});
export type AnonymizeResult = z.infer<typeof AnonymizeResult>;

export const RetentionClosedAction = z.enum(["anonymize", "delete_attachments", "delete_raw_eml"]);
export type RetentionClosedAction = z.infer<typeof RetentionClosedAction>;

export const RetentionConfig = z.object({
  /** master switch — false = cron ทำ dry-run อย่างเดียว (13 §6: dry-run ก่อนเสมอ) */
  enabled: z.boolean(),
  closed_after_days: z.object({ action: RetentionClosedAction, days: z.number().int().positive() }).nullable(),
  suspended_emails_days: z.number().int().positive(),
  audit_days: z.number().int().positive(),
  api_logs_days: z.number().int().positive(),
  guest_link_days: z.number().int().positive(),
  ai_logs_days: z.number().int().positive(),
  notifications_days: z.number().int().positive(),
  cron_runs_days: z.number().int().positive(),
});
export type RetentionConfig = z.infer<typeof RetentionConfig>;

export const RetentionRun = z.object({
  id: z.uuid(),
  policy_id: z.uuid().nullable(),
  dsar_request_id: z.uuid().nullable(),
  dry_run: z.boolean(),
  action: z.string(),
  summary: z.record(z.string(), z.unknown()),
  started_at: z.iso.datetime(),
  finished_at: z.iso.datetime().nullable(),
  error: z.string().nullable(),
});
export type RetentionRun = z.infer<typeof RetentionRun>;

export const IncidentRisk = z.enum(["low", "medium", "high", "critical"]);
export const IncidentStatus = z.enum(["open", "contained", "closed"]);

export const SecurityIncident = z.object({
  id: z.uuid(),
  detected_at: z.iso.datetime(),
  /** deadline แจ้ง PDPC = detected_at + 72 ชม. (13 §5.3) */
  pdpc_deadline_at: z.iso.datetime(),
  description: z.string(),
  affected_scope: z.string().nullable(),
  risk: IncidentRisk,
  pdpc_notified_at: z.iso.datetime().nullable(),
  subjects_notified_at: z.iso.datetime().nullable(),
  handler_user_id: z.uuid().nullable(),
  status: IncidentStatus,
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type SecurityIncident = z.infer<typeof SecurityIncident>;

export const SecurityIncidentInput = z.object({
  detected_at: z.iso.datetime(),
  description: z.string().min(1).max(5000),
  affected_scope: z.string().max(2000).optional(),
  risk: IncidentRisk.optional(),
});
export type SecurityIncidentInput = z.infer<typeof SecurityIncidentInput>;

export const SecurityIncidentPatch = z.object({
  description: z.string().min(1).max(5000).optional(),
  affected_scope: z.string().max(2000).nullable().optional(),
  risk: IncidentRisk.optional(),
  status: IncidentStatus.optional(),
  pdpc_notified_at: z.iso.datetime().nullable().optional(),
  subjects_notified_at: z.iso.datetime().nullable().optional(),
});
export type SecurityIncidentPatch = z.infer<typeof SecurityIncidentPatch>;

/** portal — เจ้าของข้อมูลขอเอง (session) */
export const MyDataRequestInput = z.object({
  type: z.enum(["access", "erase"]),
});
export type MyDataRequestInput = z.infer<typeof MyDataRequestInput>;
